import { Link } from "react-router-dom";

const styles = {
  wrapper: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
    backgroundColor: "#f5f5f7",
    minHeight: "100vh",
    color: "#1d1d1f",
  },
  nav: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    backgroundColor: "rgba(255,255,255,0.85)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    borderBottom: "1px solid rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 40px",
    height: "52px",
  },
  navLogo: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#1d1d1f",
    letterSpacing: "-0.5px",
    textDecoration: "none",
  },
  navLogoAccent: {
    color: "#2563eb",
  },
  backLink: {
    fontSize: "14px",
    color: "#2563eb",
    textDecoration: "none",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  hero: {
    backgroundColor: "#fff",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    padding: "64px 24px 48px",
    textAlign: "center",
  },
  heroLabel: {
    fontSize: "13px",
    fontWeight: "700",
    letterSpacing: "1.5px",
    color: "#2563eb",
    textTransform: "uppercase",
    marginBottom: "12px",
  },
  heroTitle: {
    fontSize: "clamp(32px, 5vw, 52px)",
    fontWeight: "800",
    letterSpacing: "-1.5px",
    color: "#1d1d1f",
    marginBottom: "16px",
  },
  heroMeta: {
    fontSize: "14px",
    color: "#9ca3af",
    marginBottom: "0",
  },
  content: {
    maxWidth: "760px",
    margin: "0 auto",
    padding: "64px 24px 96px",
  },
  section: {
    marginBottom: "48px",
  },
  sectionTitle: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#1d1d1f",
    marginBottom: "14px",
    paddingBottom: "10px",
    borderBottom: "2px solid #eff6ff",
  },
  paragraph: {
    fontSize: "16px",
    color: "#374151",
    lineHeight: 1.75,
    marginBottom: "16px",
  },
  list: {
    fontSize: "16px",
    color: "#374151",
    lineHeight: 1.9,
    paddingLeft: "24px",
    marginBottom: "16px",
  },
  highlight: {
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: "12px",
    padding: "20px 24px",
    marginBottom: "32px",
  },
  highlightText: {
    fontSize: "15px",
    color: "#1d4ed8",
    lineHeight: 1.65,
    margin: 0,
    fontWeight: "500",
  },
  footer: {
    backgroundColor: "#1d1d1f",
    color: "rgba(255,255,255,0.4)",
    textAlign: "center",
    padding: "32px 24px",
    fontSize: "13px",
  },
  footerLink: {
    color: "rgba(255,255,255,0.45)",
    textDecoration: "none",
    margin: "0 12px",
  },
};

export default function PrivacyPolicy() {
  return (
    <div style={styles.wrapper}>
      <nav style={styles.nav}>
        <Link to="/" style={styles.navLogo}>
          Om<span style={styles.navLogoAccent}>ello</span>
        </Link>
        <Link to="/" style={styles.backLink}>
          ← Back to Home
        </Link>
      </nav>

      <div style={styles.hero}>
        <p style={styles.heroLabel}>Legal</p>
        <h1 style={styles.heroTitle}>Privacy Policy</h1>
        <p style={styles.heroMeta}>
          Last updated: March 22, 2026 · Effective: March 22, 2026
        </p>
      </div>

      <div style={styles.content}>
        <div style={styles.highlight}>
          <p style={styles.highlightText}>
            🔒 Omello is built with privacy as a core principle. When you use local AI models,
            your audio, transcripts, and notes remain entirely on your device and are not transmitted to any server.
            If you opt into a cloud AI model, Omello may transmit transcript text (and relevant text prompts/questions) to the selected third-party AI provider for processing.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>1. Introduction</h2>
          <p style={styles.paragraph}>
            Omello (“we”, “our”, or “us”) respects your privacy and is committed to protecting it.
          </p>
          <p style={styles.paragraph}>
            This Privacy Policy explains how information is handled when you use the Omello macOS application.
            By using Omello, you agree to the terms of this policy.
          </p>
          <p style={styles.paragraph}>
            We may update this Privacy Policy from time to time. Changes will be reflected by the updated
            “Last updated” date.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>2. Information We Process</h2>
          <p style={styles.paragraph}>
            Omello is designed to minimize data collection. We do not require account creation and do not
            collect personal data unless explicitly provided by you.
          </p>
          <p style={styles.paragraph}>
            We collect information through your direct use of the app. For example: when you record audio, Omello generates
            live transcripts; when you answer quizzes or ask questions, Omello uses that text to generate quiz questions, AI responses,
            and study notes; and when you enter your own notes, Omello saves them with your content.
          </p>
          <ul style={styles.list}>
            <li>
              <strong>Local AI Models:</strong> All processing (audio recording, live transcription, transcript text,
              generated notes, quiz questions, and AI responses) occurs entirely on your device. No user content is
              transmitted to third parties.
            </li>
            <li>
              <strong>Cloud AI Models (User Opt-in):</strong> If you explicitly select a cloud AI model for a session,
              Omello transmits only the minimum necessary text needed to generate results (for example: your transcript text
              and the text prompt(s)/questions derived from your interactions). Audio recordings are not transmitted; only text is sent.
            </li>
            <li>
              <strong>Diagnostics (Optional):</strong> If enabled, anonymous crash reports and
              performance data may be collected to improve the app. This data does not include
              your notes, transcripts, or recordings.
            </li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>3. How Information Is Used</h2>
          <ul style={styles.list}>
            <li>Generate transcripts, notes, quizzes, and AI responses</li>
            <li>Store and organize your content locally</li>
            <li>Improve app performance and reliability</li>
            <li>Provide support if you contact us</li>
          </ul>
          <p style={styles.paragraph}>
            When you select a cloud AI model, the third-party provider generates the requested AI outputs using the transcript text and prompts sent during that request.
          </p>
          <p style={styles.paragraph}>
            We do not sell, rent, or use your data for advertising.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>4. Data Storage</h2>
          <p style={styles.paragraph}>
            All user-generated content — including audio recordings, transcripts, notes, and quizzes —
            is stored locally on your device.
          </p>
          <p style={styles.paragraph}>
            Omello does not store your user content on external servers. If you opt into cloud AI processing,
            transcript text may be transmitted during the request and handled by the selected third-party AI provider
            according to that provider's privacy policy.
          </p>
          <p style={styles.paragraph}>
            You can delete your data at any time by removing content within the app or uninstalling the application.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>5. Third-Party Services</h2>
          <p style={styles.paragraph}>
            Omello may share transcript text and related text prompts/questions with third-party AI service providers
            when you explicitly select a cloud AI model in the app.
          </p>
          <ul style={styles.list}>
            <li>
              <strong>What data is sent:</strong> transcript text and the text prompt(s)/questions required to generate
              the requested output. Audio recordings are not transmitted.
            </li>
            <li>
              <strong>Who the data is sent to:</strong> the third-party AI service provider that powers the cloud AI model
              you select in Omello (the provider name is shown in the in-app model picker before you opt in).
            </li>
            <li>
              <strong>Consent before sending:</strong> sending happens only after you explicitly select a cloud AI model
              for the session. If you choose local AI models, no third-party transmission occurs.
            </li>
            <li>
              <strong>Data protection:</strong> Omello transmits the minimum necessary data securely and relies on the
              third-party provider's privacy and security practices to protect user inputs. We require providers to use appropriate safeguards (such as encryption in transit) and to process user input for the purpose of generating the requested outputs.
              Please review the selected provider's privacy policy for details on how your data is handled, retained, and protected.
            </li>
          </ul>
          <p style={styles.paragraph}>
            We recommend avoiding sensitive or confidential content when using cloud-based features.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>6. Children’s Privacy</h2>
          <p style={styles.paragraph}>
            Omello is intended for users aged 13 and older. We do not knowingly collect personal
            information from children under 13.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>7. Security</h2>
          <p style={styles.paragraph}>
            Because data is stored locally, the security of your information depends on your device.
            We recommend enabling macOS security features such as passwords and FileVault encryption.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>8. Your Rights</h2>
          <p style={styles.paragraph}>
            You have full control over your data stored within Omello and can access, modify, or delete
            it at any time within the app.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>9. Contact</h2>
          <p style={styles.paragraph}>
            If you have any questions about this Privacy Policy, you can contact us at:
          </p>
          <p style={styles.paragraph}>
            <strong>Email:</strong> ombalar123@gmail.com
          </p>
        </div>
      </div>

      <footer style={styles.footer}>
        <p style={{ margin: "0 0 8px" }}>
          © {new Date().getFullYear()} Omello. All rights reserved.
        </p>
        <div>
          <Link to="/" style={styles.footerLink}>Home</Link>
          <Link to="/privacy-policy" style={styles.footerLink}>Privacy Policy</Link>
          <Link to="/support" style={styles.footerLink}>Support</Link>
        </div>
      </footer>
    </div>
  );
}